import {
  FileProtectOutlined,
  InfoCircleOutlined,
  MailOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { Alert, Card, Divider, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

const { Title, Paragraph, Text, Link } = Typography;

interface LicenseItem {
  name: string;
  url: string;
  license: string;
}

export default function Legal(): JSX.Element {
  const { t, i18n } = useTranslation();

  const licenses = t("legal.licenses", { returnObjects: true }) as LicenseItem[];
  const privacyPoints = t("legal.privacyPoints", { returnObjects: true }) as string[];
  const disclaimer3Items = t("legal.disclaimer3.items", { returnObjects: true }) as string[];
  const recommendationItems = t("legal.recommendation.items", { returnObjects: true }) as string[];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", maxWidth: 900 }}>
      <Card>
        <Title level={2}>
          <SafetyOutlined /> {t("legal.title")}
        </Title>
        <Alert
          message={t("legal.importantNotice.message")}
          description={t("legal.importantNotice.description")}
          type="warning"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      </Card>

      <Card
        title={
          <>
            <FileProtectOutlined /> {t("legal.imprintTitle")}
          </>
        }
      >
        <Paragraph>
          <Text strong>{t("legal.imprintPer")}</Text>
        </Paragraph>

        <Paragraph>
          {t("legal.imprintName")}
          <br />
          {t("legal.imprintNote")}
          <br />
        </Paragraph>

        <Divider />

        <Paragraph>
          <Text strong>{t("legal.contactTitle")}</Text>
          <br />
          <MailOutlined /> {t("legal.emailLabel")}{" "}
          <Link href="mailto:mkraemer.mk@gmail.com">mkraemer.mk@gmail.com</Link>
          <br />
        </Paragraph>
      </Card>

      <Card title={t("legal.licenseTitle")}>
        <Paragraph>
          <Text strong>{t("legal.licenseIntro")}</Text>
        </Paragraph>

        <ul>
          {licenses.map((item) => (
            <li key={item.name}>
              <Link href={item.url} target="_blank">
                {item.name}
              </Link>{" "}
              - {item.license}
            </li>
          ))}
        </ul>
      </Card>

      <Card title={t("legal.privacyTitle")}>
        <Alert
          message={t("legal.gdprAlert.message")}
          description={t("legal.gdprAlert.description")}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Paragraph>
          <Text strong>{t("legal.privacyIntro")}</Text>
        </Paragraph>

        <Paragraph>{t("legal.privacyDesc")}</Paragraph>

        <ul>
          {privacyPoints.map((point) => (
            <li key={point}>✅ {point}</li>
          ))}
        </ul>
      </Card>

      <Card title={t("legal.disclaimerTitle")}>
        <Paragraph>
          <Text strong>{t("legal.disclaimer1.title")}</Text>
        </Paragraph>
        <Paragraph>{t("legal.disclaimer1.body")}</Paragraph>

        <Paragraph>
          <Text strong>{t("legal.disclaimer2.title")}</Text>
        </Paragraph>
        <Paragraph>{t("legal.disclaimer2.body")}</Paragraph>

        <Paragraph>
          <Text strong>{t("legal.disclaimer3.title")}</Text>
        </Paragraph>
        <Paragraph>{t("legal.disclaimer3.body")}</Paragraph>
        <ul>
          {disclaimer3Items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <Paragraph>
          <Text strong>{t("legal.disclaimer4.title")}</Text>
        </Paragraph>
        <Paragraph>{t("legal.disclaimer4.body")}</Paragraph>

        <Alert
          message={t("legal.recommendation.message")}
          description={
            <>
              <Text>{t("legal.recommendation.intro")}</Text>
              <ul style={{ marginBottom: 0, paddingLeft: 20, marginTop: 8 }}>
                {recommendationItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          }
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Card>

      <Card>
        <Paragraph style={{ textAlign: "center", marginBottom: 0 }}>
          <Text type="secondary">
            {t("legal.stand")}{" "}
            {new Date().toLocaleDateString(i18n.resolvedLanguage === "de" ? "de-DE" : "en-US", {
              year: "numeric",
              month: "long",
            })}
          </Text>
        </Paragraph>
      </Card>
    </Space>
  );
}
